/**
 * improvementJourney.js  (platform-wide journey model)
 *
 * The connective "where are you up to, here's your next step" model that
 * now spans the WHOLE home-project lifecycle, not just backyard
 * improvements: new-home build, knockdown rebuild, house & land, first
 * home buyer, renovations, and the four improvement pillars.
 *
 * ONE stage vocabulary is used everywhere. It mirrors the homepage
 * LifecycleStagePicker (Dream / Plan / Choose / Build / Own) so a visitor
 * never has to relearn the language as they move around the site.
 *
 * Works for signed-in AND anonymous visitors: the selected stage + the
 * last project are remembered in the browser, no account needed. All copy
 * is Adelaide / South Australia framed. No em dashes.
 */

// Proven, always-valid destinations for the "get matched" action.
const MATCH_NEWHOME = '/workspace/tools/find-a-builder?type=New%20Homes&skip-matcher=1';
const MATCH_EXTENSIONS = '/workspace/tools/find-a-builder?type=Extensions&skip-matcher=1';
const MATCH_RENO = '/workspace/tools/find-a-builder?type=Renovations&skip-matcher=1';
const MATCH_HNL = '/workspace/tools/house-and-land';
const MATCH_TRADIE = '/find/category/tradies';

export const IMPROVEMENT_PROJECTS = {
  // ── Backyard improvement pillars ─────────────────────────────────
  'swimming-pools': {
    label: 'Swimming pool', pro: 'pool builder', match: MATCH_EXTENSIONS,
    pillar: '/swimming-pools', cost: '/swimming-pools/costs',
    choose: '/swimming-pools/choosing-a-builder', plan: '/swimming-pools/planning',
  },
  'decking': {
    label: 'Deck', pro: 'deck builder', match: MATCH_EXTENSIONS,
    pillar: '/decking', cost: '/decking/costs',
    choose: '/decking/choosing-a-builder', plan: '/decking/planning',
  },
  'landscaping': {
    label: 'Landscaping', pro: 'landscaper', match: MATCH_EXTENSIONS,
    pillar: '/landscaping', cost: '/landscaping/costs',
    choose: '/landscaping/choosing-a-landscaper', plan: '/landscaping/planning',
  },
  'verandahs-pergolas': {
    label: 'Verandah or pergola', pro: 'verandah builder', match: MATCH_EXTENSIONS,
    pillar: '/verandahs-pergolas', cost: '/verandahs-pergolas/costs',
    choose: '/verandahs-pergolas/choosing-a-builder', plan: '/verandahs-pergolas/planning',
  },
  // ── Renovation lifecycle ─────────────────────────────────────────
  'renovations': {
    label: 'Renovation', pro: 'renovator', match: MATCH_RENO,
    pillar: '/renovations', cost: '/renovations/costs',
    choose: '/renovations/choosing-a-renovator', plan: '/renovations/planning',
    live: '/renovations/living-through-renovation',
  },
  // ── New-home build lifecycle ─────────────────────────────────────
  'building-a-new-home': {
    label: 'New home', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/building-a-new-home', cost: '/building-a-new-home/budget',
    choose: '/building-a-new-home/choosing-a-builder', plan: '/building-a-new-home/build-timeline',
  },
  'knockdown-rebuild': {
    label: 'Knockdown rebuild', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/knockdown-rebuild', cost: '/knockdown-rebuild/costs',
    choose: '/best-builder-for-knockdown-rebuild', plan: '/knockdown-rebuild/council-approvals',
  },
  'house-and-land-adelaide': {
    label: 'House and land', pro: 'builder', match: MATCH_HNL,
    pillar: '/house-and-land-adelaide', cost: '/house-and-land-package-hidden-costs',
    choose: '/questions-to-ask-house-and-land', plan: '/house-and-land-red-flags',
  },
  'first-home-buyer-adelaide': {
    label: 'First home', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/first-home-buyer-adelaide', cost: '/first-home-buyer-house-and-land-guide',
    choose: '/best-builder-for-first-home-buyers', plan: '/first-home-owner-grant-adelaide',
  },
  // ── Remaining home-project pillars (full platform coverage) ──────
  'choosing-land': {
    label: 'Land', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/choosing-land', cost: '/choosing-land/site-costs',
    choose: '/builder-selection-checklist', plan: '/choosing-land/block-assessment',
  },
  'home-design': {
    label: 'Home design', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/home-design', cost: '/adelaide-build-cost-report-2026',
    choose: '/builder-selection-checklist',
  },
  'property-development': {
    label: 'Property development', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/property-development', choose: '/builder-selection-checklist',
  },
  'changing-builders': {
    label: 'Changing builders', pro: 'builder', match: MATCH_NEWHOME,
    pillar: '/changing-builders', choose: '/builder-selection-checklist',
  },
  'sheds-garages': {
    label: 'Shed or garage', pro: 'builder', match: MATCH_EXTENSIONS,
    pillar: '/sheds-garages', choose: '/builder-selection-checklist',
  },
  'interior-design': {
    label: 'Interior design', pro: 'designer', match: MATCH_EXTENSIONS,
    pillar: '/interior-design',
  },
};

// The ONE stage vocabulary, mirroring the homepage lifecycle picker
// (Dream / Plan / Choose / Build / Own). Keys are stable and stored in
// localStorage; labels are the short pill copy.
export const IMPROVEMENT_STAGES = [
  { key: 'dream',  label: 'Dreaming' },
  { key: 'plan',   label: 'Planning' },
  { key: 'choose', label: 'Choosing' },
  { key: 'build',  label: 'Building' },
  { key: 'own',    label: 'Living in' },
];

export const isImprovementStage = (key) => IMPROVEMENT_STAGES.some((s) => s.key === key);

const mk = (kind, title, desc, href) => (href ? { kind, title, desc, href } : null);

// ── Project-scoped next steps ──────────────────────────────────────
// kind: 'guide' (blue, keep learning) | 'match' (primary, get a real
// quote) | 'save' (green, keep progress). Cards with a missing href are
// filtered out, then the first 3 are shown.
export function getNextSteps(projectKey, stageKey) {
  const p = IMPROVEMENT_PROJECTS[projectKey];
  if (!p) return [];
  const matchHref = p.match || MATCH_EXTENSIONS;
  let candidates = [];

  switch (stageKey) {
    case 'dream':
      candidates = [
        mk('guide', `${p.label} guide`, 'See what is involved, the options and how it fits your home.', p.pillar),
        mk('guide', 'What it costs', 'Indicative 2026 Adelaide ranges so you can start a rough budget.', p.cost),
        mk('save', 'Save your ideas', 'Keep inspiration in your free workspace, saved on this device.', '/workspace/tools/favourite-homes'),
      ];
      break;
    case 'plan':
      candidates = [
        mk('guide', `${p.label} costs and budget`, 'Price bands and the factors that move the number in Adelaide.', p.cost),
        mk('guide', 'Planning and approvals', 'The SA rules, approvals and a realistic timeline for your project.', p.plan),
        mk('match', 'Get a real quote', `Get matched with an Adelaide ${p.pro} who has seen a project like yours.`, matchHref),
        mk('save', 'Track your budget', 'Log quotes and compare them against the ranges as they come in.', '/workspace/tools/cost-tracker'),
      ];
      break;
    case 'choose':
      candidates = [
        mk('guide', `Choosing a ${p.pro}`, 'What to check and the questions to ask before you sign.', p.choose),
        mk('match', `Get matched with a ${p.pro}`, `We shortlist vetted Adelaide ${p.pro}s that suit your project.`, matchHref),
        mk('save', 'Save your shortlist', 'Keep your options and quotes together in your workspace.', '/workspace/tools/find-a-builder'),
      ];
      break;
    case 'build':
      candidates = [
        p.live ? mk('guide', 'Living through your renovation', 'How to cope with the mess and disruption, and keep the job on track.', p.live) : null,
        mk('save', 'Track your project', 'Milestones, costs and documents in one place.', '/workspace/tools/cost-tracker'),
        mk('match', 'Find a tradie for finishing jobs', 'Fencing, electrical, paving and the extras that finish the job.', MATCH_TRADIE),
        p.live ? null : mk('guide', 'Re-check planning and permits', 'Make sure inspections and approvals are covered before sign-off.', p.plan),
      ];
      break;
    case 'own':
      candidates = [
        mk('guide', 'Plan your next project', 'Pools, decking, landscaping, verandahs and renovations, all in one place.', '/home-improvement'),
        mk('match', 'Find a tradie for upkeep', 'Repairs, servicing and the small jobs that keep it looking new.', MATCH_TRADIE),
        mk('save', 'Keep your home records', 'Store warranties, manuals and receipts in your workspace.', '/workspace/tools/documents'),
      ];
      break;
    default:
      candidates = [];
  }

  return candidates.filter(Boolean).slice(0, 3);
}

// ── Hub-mode next steps (cross-project, /home-improvement only) ─────
// Renovations + the build projects have their own homes, so the hub only
// covers the four backyard improvement pillars.
const HUB_PROJECT_KEYS = ['swimming-pools', 'decking', 'landscaping', 'verandahs-pergolas'];

export function getHubSteps(stageKey) {
  const projects = HUB_PROJECT_KEYS.map((k) => IMPROVEMENT_PROJECTS[k]);
  const byField = (field, suffix, descFn) =>
    projects.map((p) => mk('guide', `${p.label}${suffix}`, descFn(p), p[field])).filter(Boolean);

  switch (stageKey) {
    case 'dream':
      return byField('pillar', ' guide', (p) => `See what a ${p.label.toLowerCase()} project involves in Adelaide.`);
    case 'plan':
      return byField('cost', ' costs', () => 'Indicative 2026 Adelaide price ranges.');
    case 'choose':
      return byField('choose', ': choosing a pro', (p) => `What to check before you hire a ${p.pro}.`);
    case 'build':
      return [
        { kind: 'save', title: 'Track your project', desc: 'Milestones, costs and documents in one place.', href: '/workspace/tools/cost-tracker' },
        { kind: 'match', title: 'Find a tradie for finishing jobs', desc: 'Fencing, electrical, paving and the extras.', href: MATCH_TRADIE },
        { kind: 'match', title: 'Get matched with a builder', desc: 'Vetted Adelaide builders for your backyard project.', href: MATCH_EXTENSIONS },
      ];
    case 'own':
      return [
        { kind: 'guide', title: 'Plan your next project', desc: 'Browse all four home improvement pillars.', href: '/home-improvement' },
        { kind: 'match', title: 'Find a tradie for upkeep', desc: 'Repairs, servicing and small jobs.', href: MATCH_TRADIE },
        { kind: 'save', title: 'Keep your home records', desc: 'Warranties, manuals and receipts in your workspace.', href: '/workspace/tools/documents' },
      ];
    default:
      return [];
  }
}

export const JOURNEY_STORAGE_KEY = 'bp:improvement-journey-stage';
export const JOURNEY_PROJECT_KEY = 'bp:improvement-journey-project';
// Set to '1' only when the visitor EXPLICITLY picks a stage (homepage
// picker, a journey pill, or the resume popup question) - never on the
// passive page-context seed. Lets the resolver tell "declared" from "guessed".
export const JOURNEY_DECLARED_KEY = 'bp:journey-stage-declared';

const STAGE_LABELS = IMPROVEMENT_STAGES.reduce((m, s) => { m[s.key] = s.label; return m; }, {});
export const stageLabel = (key) => STAGE_LABELS[key] || '';

// Persist the project the visitor most recently engaged with (for the
// "pick up where you left off" resume prompt). Anonymous-safe.
export function saveJourneyProject(slug) {
  const p = IMPROVEMENT_PROJECTS[slug];
  if (!p) return;
  try {
    window.localStorage.setItem(JOURNEY_PROJECT_KEY, JSON.stringify({ slug, label: p.label, ts: Date.now() }));
  } catch { /* ignore */ }
}

// Read the combined journey state used by the resume prompt.
// Returns { stage, project: {slug,label,ts} | null }.
export function readJourneyState() {
  let stage = null;
  let project = null;
  try {
    const s = window.localStorage.getItem(JOURNEY_STORAGE_KEY);
    if (s && isImprovementStage(s)) stage = s;
    const raw = window.localStorage.getItem(JOURNEY_PROJECT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.slug && IMPROVEMENT_PROJECTS[parsed.slug]) project = parsed;
    }
  } catch { /* ignore */ }
  return { stage, project };
}
